import { useShadowDomWheelScroll } from '@frontend-platform/federation-runtime';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { OctagonAlert, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type CustomMetricFormInput,
  isCancelledError,
  METRIC_OUTCOME,
  type PipelineResult,
  type ValidateJob,
  validateAndCreate,
} from '../../../api/metrics-api';
import type { CalibrationMetric } from '../../../lib/types';
import {
  ADD_CRITERION_COPY,
  ADD_CRITERION_FIELDS,
  CRITERION_NAME_ERROR,
  CRITERION_NAME_PATTERN,
  CUSTOM_METRIC_FIELD_LIMITS,
  METRIC_OUTCOME_BANNER,
} from '../constants';
import { ValidationProgress } from './validation-progress';

export interface CriterionCreatedEvent {
  metric: Record<string, unknown>;
  outcome: PipelineResult['status'];
  similarMetricName?: string;
  message?: string;
}

interface AddCriterionModalProps {
  // { botId, botRefId, env } — DS pipeline payload context.
  context: Pick<CustomMetricFormInput, 'botId' | 'botRefId' | 'env'>;
  // Loaded metric library, for the case-insensitive name-uniqueness check.
  existingMetrics: CalibrationMetric[];
  onCreated: (event: CriterionCreatedEvent) => void;
  onClose: () => void;
}

type FormState = {
  name: string;
  description: string;
  goodBehavior: string;
  badBehavior: string;
  skipCriteriaWhen: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  goodBehavior: '',
  badBehavior: '',
  skipCriteriaWhen: '',
};

const limits = CUSTOM_METRIC_FIELD_LIMITS;

function validateNameValue(name: string, isTaken: boolean): string | null {
  const trimmed = name.trim();
  if (!trimmed) return CRITERION_NAME_ERROR.REQUIRED;
  if (trimmed.length < limits.NAME_MIN) return CRITERION_NAME_ERROR.TOO_SHORT;
  if (trimmed.length > limits.NAME_MAX) return CRITERION_NAME_ERROR.TOO_LONG;
  if (!CRITERION_NAME_PATTERN.test(trimmed)) return CRITERION_NAME_ERROR.INVALID_CHARS;
  if (isTaken) return CRITERION_NAME_ERROR.TAKEN;
  return null;
}

export function AddCriterionModal({
  context,
  existingMetrics,
  onCreated,
  onClose,
}: AddCriterionModalProps) {
  const fieldsScrollRef = useShadowDomWheelScroll<HTMLDivElement>();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [view, setView] = useState<'form' | 'progress'>('form');
  const [activeStage, setActiveStage] = useState(0);
  const [banner, setBanner] = useState<{ title: string; message?: string } | null>(null);
  const [nameTouched, setNameTouched] = useState(false);
  const jobRef = useRef<ValidateJob | null>(null);

  const isNameTaken = useMemo(() => {
    const name = form.name.trim().toLowerCase();
    if (!name) return false;
    return existingMetrics.some((m) => m.displayName?.trim().toLowerCase() === name);
  }, [form.name, existingMetrics]);

  const nameError = nameTouched ? validateNameValue(form.name, isNameTaken) : null;

  const canSubmit = useMemo(() => {
    const name = form.name.trim();
    const description = form.description.trim();
    return (
      name.length >= limits.NAME_MIN &&
      name.length <= limits.NAME_MAX &&
      CRITERION_NAME_PATTERN.test(name) &&
      !isNameTaken &&
      !!description &&
      description.length <= limits.DESCRIPTION_MAX
    );
  }, [form.name, form.description, isNameTaken]);

  const cancelJob = () => {
    jobRef.current?.cancel();
    jobRef.current = null;
  };

  // Validation jobs are session-bound: a tab close / unmount cancels the job so
  // nothing is created in the background.
  // biome-ignore lint/correctness/useExhaustiveDependencies: cancelJob only touches a ref; mount/unmount-only effect
  useEffect(() => {
    const onBeforeUnload = () => cancelJob();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      cancelJob();
    };
  }, []);

  const handleOutcome = (res: PipelineResult) => {
    switch (res?.status) {
      case METRIC_OUTCOME.SUCCESS:
        if (res.metric) onCreated({ metric: res.metric, outcome: res.status });
        break;
      case METRIC_OUTCOME.SUCCESS_SIMILAR:
        if (res.metric)
          onCreated({
            metric: res.metric,
            outcome: res.status,
            similarMetricName: res.similarMetricName,
            message: res.message,
          });
        break;
      case METRIC_OUTCOME.VALIDATION_FAILED:
        setBanner({ ...METRIC_OUTCOME_BANNER.VALIDATION_FAILED, message: res.reason });
        setView('form');
        break;
      default:
        setBanner({ ...METRIC_OUTCOME_BANNER.SYSTEM_ERROR });
        setView('form');
        break;
    }
  };

  const runPipeline = () => {
    setBanner(null);
    setView('progress');
    setActiveStage(0);

    const job = validateAndCreate({ ...form, ...context });
    jobRef.current = job;
    job.onStage((i) => setActiveStage(i));
    job.promise
      .then((res) => handleOutcome(res))
      .catch((err: unknown) => {
        if (isCancelledError(err)) return; // session-bound cancel: nothing created
        setBanner({ ...METRIC_OUTCOME_BANNER.SYSTEM_ERROR });
        setView('form');
      })
      .finally(() => {
        jobRef.current = null;
      });
  };

  const onValidate = () => {
    setNameTouched(true);
    if (!canSubmit) return;
    runPipeline();
  };

  // User aborts validation via the progress-view cross: cancel the job and
  // return to the form with all inputs intact, surfacing the interrupted banner.
  const cancelValidation = () => {
    cancelJob();
    setBanner({ ...METRIC_OUTCOME_BANNER.INTERRUPTED });
    setView('form');
  };

  const requestClose = () => {
    cancelJob();
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent
        className="max-w-xl p-0 [&>button:last-child]:hidden"
        aria-label={view === 'progress' ? ADD_CRITERION_COPY.TITLE : undefined}
      >
        {view === 'progress' ? (
          <div className="relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={cancelValidation}
                  className="absolute top-5 right-5 z-10 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                {ADD_CRITERION_COPY.CANCEL_VALIDATION_TOOLTIP}
              </TooltipContent>
            </Tooltip>
            <ValidationProgress activeStage={activeStage} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <DialogTitle className="font-semibold text-base text-foreground">
                  {ADD_CRITERION_COPY.TITLE}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  {ADD_CRITERION_COPY.SUBTITLE}
                </DialogDescription>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={requestClose}
                    className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {ADD_CRITERION_COPY.CLOSE_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Outcome banner (validation failed / system error / interrupted) */}
            {banner && (
              <div className="flex items-start gap-2 rounded-lg border border-red-600 bg-red-50 px-2.5 py-2">
                <OctagonAlert className="h-4 w-4 shrink-0 text-red-600" />
                <div className="flex-1">
                  <div className="font-semibold text-red-600 text-sm">{banner.title}</div>
                  {banner.message && (
                    <div className="mt-0.5 text-red-600 text-xs">{banner.message}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setBanner(null)}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-background shadow-sm"
                >
                  <X className="h-3 w-3 text-foreground" />
                </button>
              </div>
            )}

            {/* Fields.
                `-mx-1 px-1 py-1`: Input/Textarea draw their focus state as a
                3px ring *outside* the border box, which `overflow-y-auto`
                clips — previously there was no horizontal room at all on the
                left, so a focused field's outline appeared sliced off. The
                padding gives the ring room and the negative margin cancels it
                out visually, so nothing shifts.
                scrollRef: restores trackpad scrolling inside the shadow-DOM
                federated build — see useShadowDomWheelScroll. */}
            <div
              ref={fieldsScrollRef}
              className="-mx-1 flex max-h-[420px] flex-col gap-4 overflow-y-auto px-1 py-1"
            >
              {ADD_CRITERION_FIELDS.map((field) => {
                const value = form[field.key];
                const isName = field.key === 'name';
                const error = isName ? nameError : null;
                return (
                  <div key={field.key} className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`criterion-${field.key}`}
                      className="font-medium text-foreground text-sm"
                    >
                      {field.label}
                      {field.optional && (
                        <span className="font-normal text-muted-foreground"> (optional)</span>
                      )}
                    </label>
                    {field.type === 'text' ? (
                      <Input
                        id={`criterion-${field.key}`}
                        value={value}
                        maxLength={field.maxLength}
                        placeholder={field.placeholder}
                        aria-invalid={!!error}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, [field.key]: e.target.value }));
                          if (field.liveValidate) setNameTouched(true);
                        }}
                      />
                    ) : (
                      <Textarea
                        id={`criterion-${field.key}`}
                        value={value}
                        maxLength={field.maxLength}
                        placeholder={field.placeholder}
                        rows={field.minRows ?? 2}
                        onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                      />
                    )}
                    <div className="flex items-center justify-between gap-2">
                      {error ? <span className="text-red-600 text-xs">{error}</span> : <span />}
                      {field.showWordLimit && (
                        <span className="text-muted-foreground text-xs">
                          {value.length}/{field.maxLength}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              disabled={!canSubmit}
              onClick={onValidate}
              className="w-full rounded-lg bg-neutral-900 px-4 py-2.5 font-medium text-sm text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {ADD_CRITERION_COPY.SUBMIT}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
